import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UpdateSpeakerMut, type UpdateSpeakerInput } from '@/lib/api/monitoring/speakers';
import { UpdateRelayMut, type UpdateRelayInput } from '@/lib/api/monitoring/relays';
import { DeviceData } from '../../DevicesTab';
import { MonitoringZoneDto, AssetVideoProcessorDto } from '@/lib/api/types.gen';
import { LinkingMode } from '../types';

interface UseLinkingModeParams {
  allSpeakers: DeviceData[];
  allRelays: DeviceData[];
  monitoredCameras: any[];
  companyId: string;
  siteId: string;
  site: any;
}

const getSiteDevicesQueryKey = (companyId: string, siteId: string) => {
  return ['siteDevices', { companyId, projectId: siteId }];
};

export const useLinkingMode = ({
  allSpeakers,
  allRelays,
  monitoredCameras,
  companyId,
  siteId,
  site
}: UseLinkingModeParams) => {
  const queryClient = useQueryClient();
  const updateSpeakerMut = useMutation({
    ...UpdateSpeakerMut(),
  });
  const updateRelayMut = useMutation({
    ...UpdateRelayMut(),
  });

  const [linkingMode, setLinkingMode] = useState<LinkingMode>({
    active: false,
    deviceId: undefined,
    deviceType: undefined,
    pendingLinks: [],
  });

  const updateDeviceRelationships = useCallback((deviceId: string, deviceType: 'speaker' | 'relay', newRelatedGroups: string[]) => {
    const correctQueryKey = getSiteDevicesQueryKey(companyId, siteId);

    queryClient.setQueryData(correctQueryKey, (old: any) => {
      if (!old?.devices) {
        return old;
      }
      
      let foundDevice = false;
      const newData = {
        ...old,
        devices: old.devices.map((device: any) => {
          if (deviceType === 'speaker' && device.secondaryDevice?.speakers?.some((s: any) => s.id === deviceId)) {
            foundDevice = true;
            return {
              ...device,
              secondaryDevice: {
                ...device.secondaryDevice,
                speakers: device.secondaryDevice.speakers.map((speaker: any) => 
                  speaker.id === deviceId 
                    ? { ...speaker, relatedComponentGroups: newRelatedGroups }
                    : speaker
                )
              }
            };
          } else if (deviceType === 'relay' && device.secondaryDevice?.relays?.some((r: any) => r.id === deviceId)) {
            foundDevice = true;
            return {
              ...device,
              secondaryDevice: {
                ...device.secondaryDevice,
                relays: device.secondaryDevice.relays.map((relay: any) => 
                  relay.id === deviceId 
                    ? { ...relay, relatedComponentGroups: newRelatedGroups }
                    : relay
                )
              }
            };
          }
          return device;
        })
      };
      
      return newData;
    });

    queryClient.invalidateQueries({ 
      queryKey: correctQueryKey
    });
    // React Query will handle the state update
  }, [queryClient, companyId, siteId]);

  const handleLinkToAllCameras = useCallback((deviceId: string) => {
    const speaker = allSpeakers.find(s => s.id === deviceId);
    const relay = allRelays.find(r => r.id === deviceId);
    const device = speaker || relay;
    
    if (!device) {
      return;
    }
    
    let deviceZone = 'default';
    
    if (device.relatedComponentGroups && device.relatedComponentGroups.length > 0) {
      const existingLinkedCamera = monitoredCameras?.find(camera => 
        device.relatedComponentGroups?.includes(camera.originalDevice?.id)
      );
      
      if (existingLinkedCamera) {
        const assignedZone = site?.monitoringZones?.find((zone: MonitoringZoneDto) => 
          zone.videoProcessors?.some((vp: AssetVideoProcessorDto) => vp.videoProcessorConfigId === existingLinkedCamera.id)
        );
        deviceZone = assignedZone?.id || 'default';
      }
    }
    
    const camerasInSameZone = monitoredCameras?.filter(camera => {
      if (deviceZone === 'default') {
        return !site?.monitoringZones?.some((zone: MonitoringZoneDto) => 
          zone.videoProcessors?.some((vp: AssetVideoProcessorDto) => vp.videoProcessorConfigId === camera.id)
        );
      } else {
        return site?.monitoringZones?.some((zone: MonitoringZoneDto) => 
          zone.id === deviceZone && 
          zone.videoProcessors?.some((vp: AssetVideoProcessorDto) => vp.videoProcessorConfigId === camera.id)
        );
      }
    }) || [];
    
    const cameraSecondaryDeviceIds = camerasInSameZone.map(camera => camera.originalDevice?.id).filter(Boolean);
    
    if (speaker) {
      const input: UpdateSpeakerInput = {
        id: speaker.id || '',
        name: speaker.name || '',
        componentGroupId: speaker.componentGroupId || '',
        relatedComponentGroups: cameraSecondaryDeviceIds,
        enabled: !(speaker as any).disabled,
      };

      updateSpeakerMut.mutate(input, {
        onSuccess: () => {
          queryClient.refetchQueries({ 
            queryKey: getSiteDevicesQueryKey(companyId, siteId)
          }).then(() => {
            // React Query will handle the state update
          });
        },
        onError: () => {
          queryClient.refetchQueries({ 
            queryKey: getSiteDevicesQueryKey(companyId, siteId)
          }).then(() => {
            // React Query will handle the state update
          });
        },
      });
    } else if (relay) {
      const input: UpdateRelayInput = {
        id: relay.id || '',
        name: relay.name || '',
        componentGroupId: relay.componentGroupId,
        relatedComponentGroups: cameraSecondaryDeviceIds,
        enabled: !(relay as any).disabled,
      };

      updateRelayMut.mutate(input, {
        onSuccess: () => {
          queryClient.refetchQueries({ 
            queryKey: getSiteDevicesQueryKey(companyId, siteId)
          }).then(() => {
            // React Query will handle the state update
          });
        },
        onError: () => {
          queryClient.refetchQueries({ 
            queryKey: getSiteDevicesQueryKey(companyId, siteId)
          }).then(() => {
            // React Query will handle the state update
          });
        },
      });
    }
  }, [monitoredCameras, allSpeakers, allRelays, updateSpeakerMut, updateRelayMut, queryClient, companyId, siteId, site]);

  const handleEnterLinkingMode = useCallback((deviceId: string, deviceType: 'speaker' | 'relay') => {
    const currentDevice = deviceType === 'speaker' 
      ? allSpeakers.find(s => s.id === deviceId)
      : allRelays.find(r => r.id === deviceId);
    
    const currentLinks = currentDevice?.relatedComponentGroups || [];
    
    setLinkingMode({
      active: true,
      deviceId,
      deviceType,
      pendingLinks: [...currentLinks],
    });
  }, [allSpeakers, allRelays]);

  const handleRemoveFromZone = useCallback((deviceId: string, deviceType: 'speaker' | 'relay') => {
    const speaker = deviceType === 'speaker' ? allSpeakers.find(s => s.id === deviceId) : null;
    const relay = deviceType === 'relay' ? allRelays.find(r => r.id === deviceId) : null;
    const device = speaker || relay;
    
    if (!device) {
      return;
    }
    
    if (speaker) {
      const input: UpdateSpeakerInput = {
        id: speaker.id || '',
        name: speaker.name || '',
        componentGroupId: speaker.componentGroupId || '',
        relatedComponentGroups: [],
        enabled: !(speaker as any).disabled,
      };
      
      updateDeviceRelationships(input.id, 'speaker', input.relatedComponentGroups || []);

      updateSpeakerMut.mutate(input, {
        onSuccess: () => {
          queryClient.refetchQueries({ 
            queryKey: getSiteDevicesQueryKey(companyId, siteId)
          }).then(() => {
            // React Query will handle the state update
          });
        },
        onError: () => {
          queryClient.refetchQueries({ 
            queryKey: getSiteDevicesQueryKey(companyId, siteId)
          }).then(() => {
            // React Query will handle the state update
          });
        },
      });
    } else if (relay) {
      const input: UpdateRelayInput = {
        id: relay.id || '',
        name: relay.name || '',
        componentGroupId: relay.componentGroupId,
        relatedComponentGroups: [],
        enabled: !(relay as any).disabled,
      };
      
      updateDeviceRelationships(input.id, 'relay', input.relatedComponentGroups || []);

      updateRelayMut.mutate(input, {
        onSuccess: () => {
          queryClient.refetchQueries({ 
            queryKey: getSiteDevicesQueryKey(companyId, siteId)
          }).then(() => {
            // React Query will handle the state update
          });
        },
        onError: () => {
          queryClient.refetchQueries({ 
            queryKey: getSiteDevicesQueryKey(companyId, siteId)
          }).then(() => {
            // React Query will handle the state update
          });
        },
      });
    }
  }, [allSpeakers, allRelays, updateSpeakerMut, updateRelayMut, queryClient, companyId, siteId, updateDeviceRelationships]);

  const handleCameraClick = useCallback((cameraId: string) => {
    if (!linkingMode.active || !linkingMode.deviceId) {
      return;
    }
    
    const camera = monitoredCameras?.find(c => c.id === cameraId);
    const cameraSecondaryDeviceId = camera?.originalDevice?.id;
    
    if (!cameraSecondaryDeviceId) {
      return;
    }
    
    setLinkingMode(prevMode => {
      const isCurrentlyLinked = prevMode.pendingLinks.includes(cameraSecondaryDeviceId);
      const newPendingLinks = isCurrentlyLinked
        ? prevMode.pendingLinks.filter(id => id !== cameraSecondaryDeviceId)
        : [...prevMode.pendingLinks, cameraSecondaryDeviceId];
      
      return {
        ...prevMode,
        pendingLinks: newPendingLinks,
      };
    });
  }, [linkingMode, monitoredCameras]);

  const handleCancelLinking = useCallback(() => {
    setLinkingMode({
      active: false,
      deviceId: undefined,
      deviceType: undefined,
      pendingLinks: [],
    });
  }, []);

  const handleSaveLinking = useCallback(() => {
    if (!linkingMode.active || !linkingMode.deviceId) {
      return;
    }
    
    const speaker = linkingMode.deviceType === 'speaker' ? allSpeakers.find(s => s.id === linkingMode.deviceId) : null;
    const relay = linkingMode.deviceType === 'relay' ? allRelays.find(r => r.id === linkingMode.deviceId) : null;
    
    if (speaker) {
      const input: UpdateSpeakerInput = {
        id: speaker.id || '',
        name: speaker.name || '',
        componentGroupId: speaker.componentGroupId || '',
        relatedComponentGroups: linkingMode.pendingLinks,
        enabled: !(speaker as any).disabled,
      };
      
      updateDeviceRelationships(input.id, 'speaker', input.relatedComponentGroups || []);

      updateSpeakerMut.mutate(input, {
        onSuccess: () => {
          queryClient.refetchQueries({ 
            queryKey: getSiteDevicesQueryKey(companyId, siteId)
          }).then(() => {
            // React Query will handle the state update
          });
          setLinkingMode({
            active: false,
            deviceId: undefined,
            deviceType: undefined,
            pendingLinks: [],
          });
        },
        onError: () => {
          queryClient.refetchQueries({ 
            queryKey: getSiteDevicesQueryKey(companyId, siteId)
          }).then(() => {
            // React Query will handle the state update
          });
        },
      });
    } else if (relay) {
      const input: UpdateRelayInput = {
        id: relay.id || '',
        name: relay.name || '',
        componentGroupId: relay.componentGroupId,
        relatedComponentGroups: linkingMode.pendingLinks,
        enabled: !(relay as any).disabled,
      };
      
      updateDeviceRelationships(input.id, 'relay', input.relatedComponentGroups || []);

      updateRelayMut.mutate(input, {
        onSuccess: () => {
          queryClient.refetchQueries({ 
            queryKey: getSiteDevicesQueryKey(companyId, siteId)
          }).then(() => {
            // React Query will handle the state update
          });
          setLinkingMode({
            active: false,
            deviceId: undefined,
            deviceType: undefined,
            pendingLinks: [],
          });
        },
        onError: () => {
          queryClient.refetchQueries({ 
            queryKey: getSiteDevicesQueryKey(companyId, siteId)
          }).then(() => {
            // React Query will handle the state update
          });
        },
      });
    }
  }, [linkingMode, allSpeakers, allRelays, updateSpeakerMut, updateRelayMut, queryClient, companyId, siteId, updateDeviceRelationships]);

  return {
    linkingMode,
    handleLinkToAllCameras,
    handleEnterLinkingMode,
    handleRemoveFromZone,
    handleCameraClick,
    handleCancelLinking,
    handleSaveLinking,
  };
};